import styles from './ContactInfoForm.module.scss';

import React, { ChangeEvent, FC, useState, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionalTextArea } from 'components/OptionalTextArea';
import { Checkbox } from 'components/EnumInput';
import { emailRegex } from 'util/Regex';
import { Customer } from 'olo-service';
import { useCheckoutValidity } from '../routes/Checkout/context/CheckoutValidity';
import { useOrderDetails } from '../routes/Checkout/context/OrderDetails';
import { useCart } from 'hooks/context/cart';
import { useCustomer } from 'hooks/context/customer/customer';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useMerchant } from 'hooks/context/merchant';
import { useServiceType } from 'hooks/context/serviceType';
import { ServiceType } from 'hooks/context/serviceType/models';
import { useDebounce } from '@clover-util/use-debounce';
import { CUSTOMER_INFO_TEST_ID } from './ContactInfoForm.const';
import { formatAddress, isPhoneNumberValid } from 'util/Formatting';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useAny } from '@clover-util/use-any';
import { useAll } from '@clover-util/use-all';
import { useToggle } from '@clover-util/use-toggle';
import { ServiceTypeInstructionsBanner } from 'components/ServiceTypeInstructionsBanner';
import { CustomerInputFields } from './CustomerInputFields';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { useDelivery } from 'hooks/context/Delivery';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { Typography } from 'components/Typography/Typography';
import { format } from 'libphonenumber-js';

export const TIMEOUT = 250;
const MAXIMUM_CHARACTER_LENGTH = 2047;

export type ContactFormData = { customer: Customer; };

function customerReducer(state: Customer, { key, value }: { key: keyof Customer; value: string }): Customer {
  if (state[key] === value) return state;

  return { ...state, [key]: value };
}

export function useContactInfoFormState() {

  const { setContactFormValidity: setContactFormValidityImpl } = useCheckoutValidity();
  const { contactFormData, setContactFormData: setContactFormDataImpl, noteFormData, setNoteFormData } = useOrderDetails();
  const { customer: globalCustomer } = useCustomer();
  const { itemCount: cartItemCount } = useCart();
  const { itemCount: giftCardCount } = useGiftCardCart();

  const isOnlyDigitalCart = cartItemCount === 0 && giftCardCount > 0;

  const [note, setNote] = useState(noteFormData.note);

  const { currentServiceType, typeMetadata, update } = useServiceType();
  const { showDineInState } = useMerchantState();
  const serviceTypeNote = typeMetadata?.note;

  const availableCustomerData = useMemo(() => {
    return {
      firstName: globalCustomer?.firstName || contactFormData.customer.firstName,
      lastName: globalCustomer?.lastName || contactFormData.customer.lastName,
      phoneNumber: globalCustomer?.phoneNumber?.value ? format(globalCustomer?.phoneNumber?.value, 'E.164') : contactFormData.customer.phoneNumber,
      email: globalCustomer?.emailAddress?.value || contactFormData.customer.email,
      country: contactFormData.customer.country
    };
  }, [contactFormData.customer, globalCustomer]);

  const [customer, setCustomer] = useReducer(customerReducer, availableCustomerData);

  const setContactFormValidity = useDebounce(setContactFormValidityImpl, TIMEOUT, [setContactFormValidityImpl]);

  useEffect(() => {
    const { email, firstName, lastName, phoneNumber, country } = customer;

    // If globalCustomer exists pass the check
    const checks = [
      currentServiceType !== ServiceType.CURBSIDE || !!serviceTypeNote?.length,
      !!globalCustomer?.emailAddress?.value || emailRegex.test(email),
      !!globalCustomer?.firstName || !!firstName?.length,
      !!globalCustomer?.lastName || !!lastName?.length,
      !!globalCustomer?.phoneNumber?.value || isPhoneNumberValid(phoneNumber, country),
    ];

    if (isOnlyDigitalCart && currentServiceType === ServiceType.CURBSIDE) update(ServiceType.PICKUP);

    setContactFormValidity(checks.length === checks.filter(v => v).length);
  },[customer, setContactFormValidity, currentServiceType, serviceTypeNote, showDineInState, isOnlyDigitalCart, update, globalCustomer]);

  const setContactFormData = useDebounce(setContactFormDataImpl, TIMEOUT, [setContactFormDataImpl]);
  useEffect(() => setContactFormData({ customer }), [customer, setContactFormData, globalCustomer]);

  useEffect(() => setNoteFormData({ note }), [note, setNoteFormData]);

  return { setNote, setCustomer, serviceTypeNote, note, customer };
}

const ContactInfoForm: FC = () => {
  const { t } = useTranslation();

  const navigate = useSmartNavigate();

  const { customer: globalCustomer, chosenMarketingAllowed, setChosenMarketingAllowed, stayLoggedIn, setStayLoggedIn } = useCustomer();

  const { showDineInState, showCustomerAuthState } = useMerchantState();

  const {
    setNote,
    note,
  } = useContactInfoFormState();

  const { merchant } = useMerchant();

  const { deliveryAddress } = useDelivery();

  const { typeMetadata, isDelivery } = useServiceType();

  const setNoteFieldByTarget = useCallback(({ target }: ChangeEvent<HTMLTextAreaElement>) => setNote(target.value), [setNote]);

  /**
   *
   * BOOLEAN CHECKS
   *
   */

  const cusInfoComplete = useAll([
    !!globalCustomer?.firstName,
    !!globalCustomer?.lastName,
    !!globalCustomer?.emailAddress.value,
    !!globalCustomer?.phoneNumber.value
  ]);

  const missingCustomerInfo = useAny([
    !globalCustomer?.firstName,
    !globalCustomer?.lastName,
    !globalCustomer?.emailAddress.value,
    !globalCustomer?.phoneNumber.value
  ]);

  const isEditable = useAny([
    !globalCustomer?.emailAddress?.verified,
    !globalCustomer?.phoneNumber?.verified
  ]);

  const [showInputFields, toggleShowInputFields, setShowInputFieldsTrue] = useToggle(missingCustomerInfo);

  useEffect(() => {
    if (!globalCustomer) setShowInputFieldsTrue();
  }, [globalCustomer, setShowInputFieldsTrue]);

  /**
   *
   * CONTENT
   *
   */

  const helpText = useMemo(() => {
    if (showDineInState) return 'contactForm.helpText_dineIn';
    if (cusInfoComplete) return 'contactForm.helpText_customerExists';
    if (isDelivery) return 'contactForm.helpText_delivery';

    return 'contactForm.helpText';
  }, [cusInfoComplete, isDelivery, showDineInState]);

  const titleText = useMemo(() => {
    if (!showInputFields) return 'contactForm.title_review';

    return 'contactForm.title';
  }, [showInputFields]);

  /**
   *
   * ELEMENTS
   *
   */

  const reviewContactInfo = (
    <>
      <div className={styles.subheader} data-testid={CUSTOMER_INFO_TEST_ID.readOnly}>
        <Typography.Body className="secondary">{t('contactForm.contact_info')}</Typography.Body>
        {isEditable && <Button variant={ButtonVariant.TEXT} className={styles.button} onClick={toggleShowInputFields} data-testid={CUSTOMER_INFO_TEST_ID.edit}>{t('edit')}</Button>}
      </div>
      <div className={styles.customerWrapper}>
        <p>{globalCustomer?.firstName} {globalCustomer?.lastName}</p>
        <p>{globalCustomer?.phoneNumber?.value && format(globalCustomer?.phoneNumber?.value, 'E.164')}</p>
        <p>{globalCustomer?.emailAddress?.value}</p>
      </div>
    </>
  );

  const deliveryInfo = (
    <div className={styles.container} data-testid={CUSTOMER_INFO_TEST_ID.delivery}>
      <div className={styles.subheader}>
        <Typography.Body className="secondary">{t('contactForm.delivery')}</Typography.Body>
        <Button
          variant={ButtonVariant.TEXT}
          className={styles.button}
          onClick={() => navigate('~checkout/address')}
          data-testid={CUSTOMER_INFO_TEST_ID.deliveryEdit}
        >
          {t('edit')}
        </Button>
      </div>
      <div className={styles.wrapper}>
        <p>{formatAddress(deliveryAddress)}</p>
        {deliveryAddress?.address2 && <p>{deliveryAddress?.address2}</p>}
      </div>
      {(typeMetadata?.isContactlessDelivery !== undefined || !!typeMetadata?.note) && (
        <>
          <div className={styles.preference}>
            <Typography.Body className="secondary">{t('contactForm.preference')}:</Typography.Body>
          </div>
          <div className={styles.wrapper}>
            {typeMetadata?.isContactlessDelivery && <p>{t('deliveryAddressModal.contactless')}</p>}
            {!typeMetadata?.isContactlessDelivery && <p>{t('deliveryAddressModal.meetDoor')}</p>}
            {typeMetadata?.note && <p>"{typeMetadata?.note}"</p>}
          </div>
        </>
      )}
    </div>
  );

return (
    <div className={styles.contactInfoForm}>
      <div className={styles.heading}>
        <Typography.Heading1>{t(titleText)}</Typography.Heading1>
      </div>

      <p className={styles.helpText} data-testid={CUSTOMER_INFO_TEST_ID.helpText}>{t(helpText)}</p>

      <ServiceTypeInstructionsBanner />

      {showInputFields && <CustomerInputFields />}
      {!showInputFields && reviewContactInfo}

      {showCustomerAuthState && !globalCustomer?.marketingAllowed && <Checkbox
        className={styles.checkboxField}
        label={t('contactForm.marketingAllowed', { merchantName: merchant?.name })}
        name={'marketingAllowed'}
        checked={chosenMarketingAllowed}
        onChange={() => setChosenMarketingAllowed(!chosenMarketingAllowed)}
        data-testid={CUSTOMER_INFO_TEST_ID.marketingAllowed}
        labelClassName={styles.checkboxLabel}

      >
      </Checkbox>}
      {showCustomerAuthState && globalCustomer && <Checkbox
          className={styles.checkboxField}
          label={t('contactForm.stayLoggedIn')}
          name={'stayLoggedInCheckBox'}
          checked={stayLoggedIn}
          onChange={() => {
            setStayLoggedIn(!stayLoggedIn);
          }}
          data-testid={CUSTOMER_INFO_TEST_ID.stayLoggedIn}
          labelClassName={styles.checkboxLabel}
      >
      </Checkbox>}
      <OptionalTextArea
        className={styles.specialInstructions}
        placeholder={t('contactForm.placeholder')}
        value={note}
        onChange={setNoteFieldByTarget}
        data-testid={CUSTOMER_INFO_TEST_ID.specialInstructionTextField}
        maxLength={MAXIMUM_CHARACTER_LENGTH}
      >
        {t('contactForm.specialInstructions')}
      </OptionalTextArea>

      {isDelivery && deliveryInfo}
    </div>
  );
};

export { ContactInfoForm };
